import { Building2, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useKeycloakAuth } from '@/contexts/keycloak-auth-context';

interface RegistrationTypeSelectorProps {
  onSelectType: (type: 'individual' | 'business') => void;
}

export function RegistrationTypeSelector({ onSelectType }: RegistrationTypeSelectorProps) {
  const { login } = useKeycloakAuth();

  return (
    <div className="container mx-auto px-4 py-12 min-h-screen flex justify-center animate-fade-slide-in">
      <div className="w-full max-w-5xl mx-auto space-y-8">
        {/* Header */}
        <div className="space-y-2 text-center">
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
            Create Your SteerFi Account
          </h1>
          <p className="text-muted-foreground text-lg">
            Choose the type of account you'd like to create
          </p>
        </div>

        {/* Registration Type Cards */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Individual Registration Card */}
          <Card
            className="cursor-pointer transition-all bg-card/50 border-border/60 backdrop-blur-sm hover:border-primary hover:shadow-lg"
            onClick={() => onSelectType('individual')}
          >
            <CardHeader>
              <div className="flex items-start space-x-4">
                <div className="rounded-sm bg-primary/10 p-3">
                  <User className="h-8 w-8 text-primary" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-xl">Individual Account</CardTitle>
                  <CardDescription className="mt-1">
                    For personal use and individual payments
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center">
                  <span className="mr-2">✓</span>
                  Personal profile management
                </li>
                <li className="flex items-center">
                  <span className="mr-2">✓</span>
                  Individual ACH payment setup
                </li>
                <li className="flex items-center">
                  <span className="mr-2">✓</span>
                  Simple account management
                </li>
                <li className="flex items-center">
                  <span className="mr-2">✓</span>
                  Quick registration process
                </li>
              </ul>
              <Button className="w-full" size="lg" onClick={() => onSelectType('individual')}>
                Register as Individual
              </Button>
            </CardContent>
          </Card>

          {/* Business Registration Card */}
          <Card
            className="cursor-pointer transition-all bg-card/50 border-border/60 backdrop-blur-sm hover:border-primary hover:shadow-lg"
            onClick={() => onSelectType('business')}
          >
            <CardHeader>
              <div className="flex items-start space-x-4">
                <div className="rounded-sm bg-primary/10 p-3">
                  <Building2 className="h-8 w-8 text-primary" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-xl">Business Account</CardTitle>
                  <CardDescription className="mt-1">
                    For organizations and companies
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center">
                  <span className="mr-2">✓</span>
                  Full business profile setup
                </li>
                <li className="flex items-center">
                  <span className="mr-2">✓</span>
                  Corporate ACH payments
                </li>
                <li className="flex items-center">
                  <span className="mr-2">✓</span>
                  Team collaboration features
                </li>
                <li className="flex items-center">
                  <span className="mr-2">✓</span>
                  Advanced payment management
                </li>
              </ul>
              <Button className="w-full" size="lg" onClick={() => onSelectType('business')}>
                Register as Business
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <button
            type="button"
            onClick={() => {
              void login();
            }}
            className="font-medium text-primary hover:underline"
          >
            Sign in
          </button>
        </div>
      </div>
    </div>
  );
}
